import { Chip, styled, useTheme } from "@mui/material";

import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { isPropValid } from "Infrastructure/Utils/StyleUtils";
import { RegistrationRequestStatus } from "RegistrationRequests/Api/registrationRequestApi";

interface RegistrationRequestStatusChipProps {
  status: RegistrationRequestStatus;
}

const StyledChip = styled(Chip, { shouldForwardProp: isPropValid })<{
  $backgroundColor: string;
  $textColor: string;
}>`
  background-color: ${(props) => props.$backgroundColor};
  color: ${(props) => props.$textColor};
  font-size: 12px;
  font-weight: 400;

  .MuiChip-label {
    padding: ${(props) => props.theme.spacing(0, Spacing.M)};
  }
`;

const STATUS_LABELS: Record<
  RegistrationRequestStatus,
  (t: ReturnType<typeof useResource>["t"]) => string
> = {
  [RegistrationRequestStatus.Pending]: (t) =>
    t(Resources.RegistrationRequests.StatusPending),
  [RegistrationRequestStatus.Resolved]: (t) =>
    t(Resources.RegistrationRequests.StatusResolved),
  [RegistrationRequestStatus.Approved]: (t) =>
    t(Resources.RegistrationRequests.StatusApproved),
  [RegistrationRequestStatus.Rejected]: (t) =>
    t(Resources.RegistrationRequests.StatusRejected),
};

export const RegistrationRequestStatusChip = ({
  status,
}: RegistrationRequestStatusChipProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();

  const colorMap: Record<RegistrationRequestStatus, string> = {
    [RegistrationRequestStatus.Pending]: theme.palette.warning.dark,
    [RegistrationRequestStatus.Resolved]: theme.palette.success.dark,
    [RegistrationRequestStatus.Approved]: theme.palette.info.dark,
    [RegistrationRequestStatus.Rejected]: theme.palette.error.dark,
  };

  return (
    <StyledChip
      label={STATUS_LABELS[status](t)}
      $backgroundColor={colorMap[status]}
      $textColor={theme.palette.text.primary}
    />
  );
};
